<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 my-4">
      <div class="col my-3">
        <h1>Estimate Templates
          <b-button class="ml-3" variant="outline-orange" @click="addEstimateTemplate()">
            <font-awesome-icon class="orange" icon="plus"/>
            New
          </b-button></h1>
      </div>
    </div>
    <div class="container-fluid">
      <div class="row estimate-template-header">
        <div class="col-3">Template Name</div>
        <div class="col-3">Codes</div>
        <div class="col-3">Diagnostic Codes</div>
        <div class="col-3"></div>
      </div>
      <div v-for="template in templates" :key="template.organizationEstimateTemplateId" class="row estimate-template-row align-items-center">
        <div class="column col-3">{{ template.name }}</div>
        <div class="column col-3 line-codes"><div v-for="(line, index) in template.lines" :key="'line-' + index">{{displayCode(line)}}</div></div>
        <div class="column col-3">
        <div v-if="template.primaryIcdCmCodeId || template.secondaryIcdCmCodeId">{{ formatICD10(template.primaryIcdCmCode.code) }} <div v-if="template.secondaryIcdCmCodeId">{{formatICD10(template.secondaryIcdCmCode.code)}}</div></div>
        </div>
          <div class="column col-3 text-right">
          <button title="Edit" @click.prevent="editEstimateTemplate(template)" type="button" class="btn btn-outline-orange m-1 wide-button">
            <font-awesome-icon class="orange mr-2" :icon="['far', 'edit']"/>Edit
          </button>
          <button title="Delete" @click.prevent="confirmDelete(template.organizationEstimateTemplateId)" type="button" class="btn btn-outline-orange m-1 wide-button">
            <font-awesome-icon class="orange mr-2" :icon="['far', 'trash-alt']"/>Delete
          </button>
        </div>
      </div>
    </div>
    <div class="alert alert-secondary" role="alert" v-show="!templates.length && !generalErrorMessage">
      No estimate templates have been configured.
    </div>
    <div class="row justify-content-center m-2">
      <div class="alert alert-danger col-6" role="alert" v-show="generalErrorMessage">
        {{ generalErrorMessage }}
      </div>
    </div>
    <estimate-template-modal :edit-estimate-template="editingEstimateTemplate"
                             @updated="estimateTemplateUpdated"
                             :visible="showingEstimateTemplateModal"
                             @update:visible="visible => showingEstimateTemplateModal = visible"
    >
    </estimate-template-modal>
    <confirm-modal :title="confirmTitle"
                   @confirmed="deleteConfirmed"
                   confirmButton="Delete"
                   :copy="confirmCopy"
                   :visible="showConfirmation"
                   @update:visible="visible => showConfirmation = visible"></confirm-modal>
  </div>
</template>
<script>
  import EstimateTemplateModal from '@/components/EstimateTemplateModal.vue'
  import api from '@/services/api'
  import ConfirmModal from '@/components/ConfirmModal.vue'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  export default {
    name: 'EstimateTemplates',
    mixins: [ErrorsMixin],
    components: { ConfirmModal, EstimateTemplateModal },
    data () {
      return {
        templates: [],
        isLoading: true,
        editingEstimateTemplate: {
          ascFees: [],
          ascLines: [],
          coProviders: [],
          fees: [],
          hasImplant: false,
          implantCost: null,
          implantDescription: null,
          lines: [],
          name: '',
          organizationEstimateSurveySet: null,
          organizationEstimateSurveySetId: null,
          organizationEstimateTemplateId: 0,
          primaryIcdCmCode: {},
          primaryIcdCmCodeId: null,
          secondaryIcdCmCode: {},
          secondaryIcdCmCodeId: null
        },
        showingEstimateTemplateModal: false,
        estimateTemplateId: 0,
        confirmingDelete: true,
        showConfirmation: false
      }
    },

    computed: {
      confirmTitle () {
        return 'Delete Estimate Template'
      },

      confirmCopy () {
        if (this.confirmingDelete) {
          return 'Are you sure you want to delete this estimate template?'
        }
        return ''
      }
    },

    methods: {
      formatICD10 (code) {
        if (code.length > 6) {
          return code.substr(0, 3) + '.' + code.substr(3, 3) + '.' + code.substr(6)
        } else if (code.length > 3) {
          return code.substr(0, 3) + '.' + code.substr(3)
        }
        return code
      },
      displayCode (code) {
        if (code.modifier) {
          return code.code + '-' + code.modifier
        }

        return code.code
      },
      loadEstimateTemplates () {
        this.isLoading = true
        const parameters = {
          pageSize: 1000,
          page: 1,
          totalTemplates: 1,
          templates: []
        }
        api.post('/Dashboard/EstimateTemplates', parameters).then(response => {
          this.isLoading = false
          this.templates = response.data.templates
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to load appointments: ' + error.message, error)
            this.errors = ['Error loading appointments: ' + error.message]
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },
      addEstimateTemplate () {
        this.editingEstimateTemplate = {
          ascFees: [],
          ascLines: [],
          coProviders: [],
          fees: [],
          hasImplant: false,
          implantCost: null,
          implantDescription: null,
          lines: [],
          name: '',
          organizationEstimateSurveySet: null,
          organizationEstimateSurveySetId: null,
          organizationEstimateTemplateId: 0,
          primaryIcdCmCode: {},
          primaryIcdCmCodeId: null,
          secondaryIcdCmCode: {},
          secondaryIcdCmCodeId: null
        }
        this.showingEstimateTemplateModal = true
      },
      editEstimateTemplate (template) {
        this.editingEstimateTemplate = template
        this.showingEstimateTemplateModal = true
      },
      confirmDelete (template) {
        this.showConfirmation = true
        this.estimateTemplateId = template
      },
      deleteConfirmed (confirmation) {
        this.showConfirmation = false
        if (confirmation === true) {
          this.deleteEstimateTemplate()
        }
      },

      deleteEstimateTemplate () {
        if (this.confirmingDelete) {
          api.delete('/Dashboard/EstimateTemplate/' + this.estimateTemplateId).then(() => {
            this.loadEstimateTemplates()
          }).catch(error => {
            if (!this.$store.canHandleNetworkError(error)) {
              this.$log.error('Error attempting to delete custom notification: ' + error.message, error)
            }
          })
          this.estimateTemplateId = 0
        }
      },
      estimateTemplateUpdated () {
        this.showingEstimateTemplateModal = false
        this.loadEstimateTemplates()
      }
    },
    created () {
      this.loadEstimateTemplates()
    }
  }
</script>

<style scoped>
.estimate-template-header div {
  padding: 0.75rem;
  vertical-align: bottom;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
  border-collapse: collapse;
}
.estimate-template-row .column {
  padding: 0.75rem;
  vertical-align: bottom;
  border-collapse: collapse;
}

.estimate-template-row {
  border-bottom: 1px solid #dee2e6;
  background-color: #f9f9f9;
  /*cursor: pointer;*/
}
</style>
